import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, DatePicker, Dropdown } from "antd"
import { useMatch, useNavigate } from "react-router-dom"
import { oldRoutes } from "router/old-routes"
import useForm from "hooks/useForm"
import useSearchFilter, { toAccessSearchString } from "hooks/useSearchFilter"
import { TRIP_FILTERS } from "constants/trip"
import styles from "layoutsV2/client/GuestHeader/style.module.css"
import GooglePlacesInput from "modules/google/GooglePlacesInput"
import { SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { setGuestCount, setCalendarDates } from "store/trips"
const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"

SearchTripsAntd.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool
}

export default function SearchTripsAntd({ className, disabled = false }) {
  const navigate = useNavigate()
  const placeInputRef = useRef()
  const isTripsPage = useMatch(oldRoutes.tripsPath())
  const [params, updateSearchFilters] = useSearchFilter({ accessAttributes: TRIP_FILTERS })
  const [form, changeHandler, submitHandler, submitCallback, reset, isChanged] = useForm(params)
  const dispatch = useDispatch()
  const guests = useSelector((state) => state.trips.guests)
  const { t } = useTranslation()
  const [adults, setAdults] = useState(guests.adults || 0)
  const [children, setChildren] = useState(guests.children || 0)
  // A location picked from the dropdown should let the user run the search even when it
  // matches the location already in the URL (e.g. re-selecting the same recent search),
  // where the form-vs-params diff (isChanged) alone would leave the button disabled.
  const [isLocationPicked, setIsLocationPicked] = useState(false)
  // Range picker is controlled (value + open) so an end-only edit can be applied and closed by us.
  const [dateRange, setDateRange] = useState(null)
  const [isRangeOpen, setIsRangeOpen] = useState(false)
  const rangePickerRef = useRef(null)
  const isEndOnlyPickRef = useRef(false)

  // antd's RangePicker refocuses "From" right after "To" is picked. That focus call is skipped for an
  // end-only pick (see handleCalendarChange) so the cursor never flashes into the start field.
  useEffect(() => {
    const startInput = rangePickerRef.current?.nativeElement?.querySelector("input")
    if (!startInput) return undefined
    startInput.focus = (options) => {
      if (!isEndOnlyPickRef.current) HTMLInputElement.prototype.focus.call(startInput, options)
    }
    return () => {
      delete startInput.focus
    }
  }, [])

  // Store initial coordinates when component mounts (only set once)
  const initialCoordinates = useRef(null)
  if (!initialCoordinates.current && (params.lat || params.lng)) {
    initialCoordinates.current = {
      lat: params.lat,
      lng: params.lng
    }
  }

  useEffect(() => {
    setAdults(guests.adults || 0)
    setChildren(guests.children || 0)
  }, [guests.adults, guests.children])

  const placeChangedHandler = (event) => {
    const { value, coordinates, bounds } = event.target

    // Update address
    changeHandler({ target: { name: "address", value } })

    // Update coordinates if available
    if (coordinates) {
      setIsLocationPicked(true)
      changeHandler({ target: { name: "lat", value: coordinates.lat } })
      changeHandler({ target: { name: "lng", value: coordinates.lng } })
    }

    // Carry the place's viewport so the trips map can frame the whole region
    // (e.g. the full Bahamas island chain) instead of the center point at a fixed zoom.
    if (bounds) {
      changeHandler({ target: { name: "northeast_corner", value: bounds.northeast_corner } })
      changeHandler({ target: { name: "southwest_corner", value: bounds.southwest_corner } })
    }
  }

  submitCallback(() => {
    if (disabled) return // Prevent navigation if disabled
    setIsLocationPicked(false)
    const search = toAccessSearchString(form, TRIP_FILTERS)
    navigate({ pathname: oldRoutes.tripsPath(), search })
  })

  // Allow submitting a re-picked location even when the form otherwise matches the URL params.
  const handleSubmit = (event) => submitHandler(event, isLocationPicked)

  // Removed: This was causing auto-revert when user searches
  // The GooglePlacesInput component manages its own value
  useEffect(() => {
    if (!isTripsPage) {
      reset()
      setIsLocationPicked(false)
      updateSearchFilters({ guest_count: "", from_date: "", to_date: "" })
      if (placeInputRef.current) placeInputRef.current.value = ""
    }
  }, [isTripsPage])

  const handleGuestChange = (type, guestType) => {
    let newAdults = adults
    let newChildren = children

    if (type === "increase" && guestType === "adults") {
      newAdults = adults + 1
    } else if (type === "increase") {
      newChildren = children + 1
    } else if (guestType === "adults") {
      newAdults = Math.max(0, adults - 1)
    } else {
      newChildren = Math.max(0, children - 1)
    }

    const totalGuests = newAdults + newChildren

    // Update Redux store
    dispatch(setGuestCount({ adults: newAdults, children: newChildren }))

    // Update form values (so submit uses guest_count)
    changeHandler({ target: { name: "adults", value: newAdults } })
    changeHandler({ target: { name: "children", value: newChildren } })
    changeHandler({ target: { name: "guest_count", value: totalGuests } })

    // keep local UI state in sync
    setAdults(newAdults)
    setChildren(newChildren)
  }

  const handleClearGuests = () => {
    dispatch(setGuestCount({ adults: 0, children: 0 }))
    changeHandler({ target: { name: "adults", value: 0 } })
    changeHandler({ target: { name: "children", value: 0 } })
    changeHandler({ target: { name: "guest_count", value: 0 } })
    setAdults(0)
    setChildren(0)
  }

  const classes = [styles.antdSearchTrips, "flex-wrap"]
  if (className) classes.push(className)

  const guestDropdownContent = (
    <div className={styles.guestDropdownMenu}>
      <div className={styles.guestCategory}>
        <span>{t("filterTags.adults")}</span>
        <div className={styles.guestCounter}>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("decrease", "adults")} disabled={adults <= 0}>
            −
          </Button>
          <span>{adults}</span>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("increase", "adults")}>
            +
          </Button>
        </div>
      </div>

      <div className={styles.guestCategory}>
        <span>{t("filterTags.children")}</span>
        <div className={styles.guestCounter}>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("decrease", "children")} disabled={children <= 0}>
            −
          </Button>
          <span>{children}</span>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("increase", "children")}>
            +
          </Button>
        </div>
      </div>

      <div className={styles.clearLink}>
        <Button type="link" onClick={handleClearGuests}>
          {t("filterTags.clear")}
        </Button>
      </div>
    </div>
  )

  const handleDateChange = (dates) => {
    setDateRange(dates)
    if (dates?.length === 2) {
      const from = dates[0].format("YYYY-MM-DD")
      const to = dates[1].format("YYYY-MM-DD")
      dispatch(
        setCalendarDates({
          from_date: from,
          to_date: to
        })
      )
      // mirror into form so submit constructs correct search string
      changeHandler({ target: { name: "from_date", value: from } })
      changeHandler({ target: { name: "to_date", value: to } })
    } else {
      dispatch(
        setCalendarDates({
          from_date: "",
          to_date: ""
        })
      )
      changeHandler({ target: { name: "from_date", value: "" } })
      changeHandler({ target: { name: "to_date", value: "" } })
    }
  }

  // antd's RangePicker jumps to "From" after "To" is picked, forcing a start re-pick even when
  // only the end date should change. When a start already exists, block that jump, apply
  // [start, new end] ourselves, then close the popup and drop focus (the clicked panel holds it in a
  // real browser). Applying it here rather than waiting for antd's own commit matters: antd's
  // post-close reset would otherwise put the previous end date back.
  const handleCalendarChange = (dates, _dateStrings, info) => {
    if (!isRangeOpen || info?.range !== "end" || !dates?.[0] || !dates?.[1]) return
    const [start, end] = dates
    isEndOnlyPickRef.current = true
    setTimeout(() => {
      isEndOnlyPickRef.current = false
      handleDateChange(end.isBefore(start, "day") ? [end, start] : [start, end])
      setIsRangeOpen(false)
      rangePickerRef.current?.blur()
      if (document.activeElement?.closest(".ant-picker-dropdown")) document.activeElement.blur()
    })
  }

  return (
    <form className={classes.join(" ")} onSubmit={handleSubmit}>
      <div className={styles.searchBox}>
        <div className={styles.inputWrapper}>
          <SearchOutlined className={styles.icon} />
          <GooglePlacesInput
            placeholder={t("filterTags.any_location")}
            className={styles.input}
            defaultValue={form.address}
            onChange={placeChangedHandler}
            wrapperProps={{ className: styles.inputInner }}
            ref={placeInputRef}
            updateSearchFilters={updateSearchFilters}
            initialCoordinates={initialCoordinates.current}
            usePredictionDescription
            disabled={disabled}
          />
        </div>
        <div className={styles.divider} />
        <Dropdown className={styles.guestDropdown} overlay={guestDropdownContent} trigger={disabled ? [] : ["click"]} placement="bottomLeft" disabled={disabled}>
          <Button className={styles.input} disabled={disabled}>
            {adults + children > 0 ? `${adults + children} ${t("filterTags.guests")}` : t("filterTags.guests")}
          </Button>
        </Dropdown>

        <div className={styles.divider} />

        <div className={styles.inputWrapper}>
          <RangePicker
            ref={rangePickerRef}
            value={dateRange}
            open={isRangeOpen}
            onOpenChange={setIsRangeOpen}
            className={styles.clientRangePicker}
            format={dateFormat}
            onChange={handleDateChange}
            onCalendarChange={handleCalendarChange}
            placeholder={[t("global.from"), t("global.to")]}
            disabled={disabled}
          />
        </div>

        <Button type="primary" htmlType="submit" className={styles.searchButton} disabled={disabled || (!isChanged && !isLocationPicked)}>
          {t("global.search")}
        </Button>
      </div>
    </form>
  )
}
